import React, { useState, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Sparkles, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { services } from '../mock/mock';
import { submitLead } from '../services/api';

export default function ServicePage() {
  const { slug } = useParams();
  const service = useMemo(() => services.find((s) => s.slug === slug), [slug]);
  const others = useMemo(() => services.filter((s) => s.slug !== slug), [slug]);

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!service) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Please add your name and email'); return; }
    setLoading(true);
    try {
      await submitLead({
        ...form,
        goal: service.slug,
        source: `service-${service.slug}`,
      });
      toast.success(`Thanks! Sam will be in touch about ${service.title}.`);
      setDone(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  };

  return (
    <main data-testid={`service-page-${service.slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden bg-black">
        <div className="absolute inset-0">
          <img src={service.img} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-brand text-xs uppercase tracking-[0.25em] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="mt-6 flex items-center gap-3">
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand font-bold">Service</div>
            <div className="w-8 h-px bg-brand/60" />
            <div className="text-[11px] tracking-[0.3em] uppercase text-white/50">{service.fromPrice}</div>
          </div>
          <h1 className="mt-4 font-display italic text-white uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.9]">
            {service.title.split(' ').map((w, i, arr) => (
              <React.Fragment key={i}>
                {i === arr.length - 1 ? <span className="text-brand">{w}</span> : <>{w} </>}
              </React.Fragment>
            ))}
          </h1>
          <p className="mt-6 max-w-2xl text-white/75 text-lg leading-relaxed">{service.longDesc}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {service.tags.map((t) => (
              <Badge key={t} className="bg-white/10 text-white border border-white/15 rounded-none text-[10px] tracking-widest uppercase">{t}</Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Real results */}
      {service.results?.length > 0 && (
        <section className="py-24 bg-[#0d0d10] border-t border-white/5">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Real Clients · Real Work</div>
                <h2 className="mt-3 font-display italic text-white text-5xl md:text-6xl uppercase leading-[0.9]">
                  Earnt, <span className="text-brand">not given</span>
                </h2>
              </div>
              <p className="max-w-md text-white/60 text-sm">Every transformation below started with the same first step, a message. Consistency, honest check-ins and coaching around real life did the rest.</p>
            </div>

            <div className="mt-12 grid md:grid-cols-2 gap-6 md:gap-8">
              {service.results.map((r, i) => (
                <figure key={i} className="reveal lift group relative">
                  <div className="absolute -top-3 -left-3 z-10 bg-brand text-white text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1 border-2 border-black">
                    Result 0{i + 1}
                  </div>
                  <div className="relative overflow-hidden bg-black border border-white/5">
                    <img src={r.img} alt={r.caption} loading="lazy" className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-700" />
                    <div className="pointer-events-none absolute inset-y-0 left-1/2 w-px bg-brand/40" aria-hidden />
                    <div className="pointer-events-none absolute top-3 left-3 text-[10px] tracking-[0.3em] uppercase text-white/85 bg-black/70 px-2 py-1">Before</div>
                    <div className="pointer-events-none absolute top-3 right-3 text-[10px] tracking-[0.3em] uppercase text-white bg-brand px-2 py-1">After</div>
                  </div>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <div className="h-px w-8 bg-brand" />
                    <span className="text-white/80 text-sm uppercase tracking-[0.2em]">{r.caption}</span>
                  </figcaption>
                </figure>
              ))}
            </div>

            <div className="mt-14 text-center reveal">
              <Link to="/transform" className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-brand hover:gap-3 transition-all">
                See more transformations <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Community gallery */}
      {service.community?.photos?.length > 0 && (
        <section className="py-24 bg-black border-t border-white/5">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-brand">More Than A Class</div>
                <h2 className="mt-3 font-display italic text-white text-5xl md:text-6xl uppercase leading-[0.9]">
                  {service.community.heading} <span className="text-brand">{service.community.accent}</span>
                </h2>
              </div>
              <p className="max-w-md text-white/60 text-sm">{service.community.tagline}</p>
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {service.community.photos.map((p, i) => (
                <figure
                  key={i}
                  className={`reveal group relative overflow-hidden border border-white/5 ${i === 0 ? 'col-span-2 row-span-2 aspect-square md:aspect-[4/5]' : 'aspect-square'}`}
                >
                  <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-90" />
                  <figcaption className="absolute bottom-3 left-3 right-3 flex items-center gap-2 text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-white">
                    <span className="w-6 h-px bg-brand" />
                    <span>{p.label}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Client story testimonial */}
      {service.testimonials?.length > 0 && (
        <section className="py-24 bg-[#0d0d10] border-t border-white/5 overflow-hidden">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="reveal">
              <div className="text-[11px] tracking-[0.3em] uppercase text-brand">A Client's Words</div>
              <h2 className="mt-3 font-display italic text-white text-5xl md:text-6xl uppercase leading-[0.9]">
                Real journey, <span className="text-brand">real change</span>
              </h2>
            </div>

            {service.testimonials.map((t, i) => (
              <div key={i} className="mt-14 grid lg:grid-cols-12 gap-8 md:gap-12 items-start">
                <div className="lg:col-span-6 reveal">
                  <figure className="relative">
                    <div className="absolute -top-4 -left-4 z-10 bg-brand text-white text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1.5 border-2 border-black">
                      Before · After
                    </div>
                    <img src={t.img} alt="Client transformation before and after" loading="lazy" className="w-full h-auto block border border-white/5" />
                    {(t.beforeDate || t.afterDate) && (
                      <div className="mt-4 flex items-center justify-between gap-4 text-[10px] tracking-[0.3em] uppercase text-white/60">
                        <span className="flex items-center gap-2"><span className="w-6 h-px bg-white/30" /> {t.afterDate}</span>
                        <span className="flex items-center gap-2">{t.beforeDate} <span className="w-6 h-px bg-white/30" /></span>
                      </div>
                    )}
                  </figure>
                </div>

                <div className="lg:col-span-6 reveal">
                  {(t.stat || t.stat2) && (
                    <div className="grid grid-cols-2 gap-4 mb-8">
                      {t.stat && (
                        <div className="bg-brand text-white p-5 border-2 border-black relative overflow-hidden">
                          <div className="font-display italic text-6xl md:text-7xl leading-none">{t.stat}</div>
                          <div className="mt-1 text-[10px] tracking-[0.3em] uppercase text-white/85">{t.statUnit}</div>
                          <div className="absolute -right-3 -bottom-3 font-display italic text-white/10 text-7xl select-none">↓</div>
                        </div>
                      )}
                      {t.stat2 && (
                        <div className="bg-black border-2 border-brand p-5 text-white relative">
                          <div className="font-display italic text-6xl md:text-7xl leading-none">{t.stat2}</div>
                          <div className="mt-1 text-[10px] tracking-[0.3em] uppercase text-white/70">{t.stat2Unit}</div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="relative">
                    <blockquote className="relative text-white/85 text-base md:text-lg leading-relaxed">
                      {t.quote}
                    </blockquote>
                  </div>

                  <div className="mt-6 flex items-center gap-3">
                    <div className="h-px w-10 bg-brand" />
                    <span className="text-[11px] tracking-[0.3em] uppercase text-white/60">Mum & Baby client · 3+ years with Sam</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Highlights + Booking form */}
      <section className="py-20 bg-[#0a0a0d] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6 reveal">
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand">What's included</div>
            <h2 className="mt-3 font-display italic text-white text-4xl md:text-5xl uppercase leading-[0.95]">
              Everything you get
            </h2>
            <div className="mt-6 h-1 w-20 bg-brand" />
            <ul className="mt-8 space-y-3">
              {service.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-white/85">
                  <div className="w-6 h-6 grid place-items-center bg-brand text-white flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                  <span>{h}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 bg-brand/10 border border-brand/30 p-5">
              <div className="flex items-center gap-2 text-brand text-[11px] uppercase tracking-widest font-bold">
                <Sparkles className="w-4 h-4" /> Ideal for
              </div>
              <p className="mt-2 text-white/85">{service.ideal}</p>
            </div>

            <div className="mt-8 text-xs text-white/50 uppercase tracking-[0.2em]">
              Prefer to browse classes? <Link to="/transform" className="text-brand hover:underline">See the weekly timetable</Link>
            </div>
          </div>

          <div className="lg:col-span-6 reveal">
            <Card id="book" className="bg-[#111116] border border-white/10 rounded-none p-6 md:p-8">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 grid place-items-center brand-gradient">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-brand">Book your spot</div>
                  <div className="font-display italic uppercase text-white text-2xl leading-tight">Enquire, {service.title}</div>
                </div>
              </div>
              <p className="text-white/60 text-sm mt-3">Drop your details and I'll come back within 24 hours with the best next step, availability, and a quote if a tailored programme suits you better.</p>

              {done ? (
                <div className="mt-8 text-center py-8" data-testid="service-form-success">
                  <div className="w-14 h-14 rounded-full brand-gradient grid place-items-center mx-auto"><Check className="w-6 h-6 text-white" /></div>
                  <div className="font-display uppercase text-2xl text-white mt-4">You're in.</div>
                  <p className="text-white/60 mt-2">Sam will be in touch shortly.</p>
                  <Button onClick={() => setDone(false)} variant="outline" className="mt-6 bg-transparent border-white/25 text-white hover:bg-white hover:text-black uppercase tracking-widest text-xs rounded-none px-6 py-5">
                    Send another
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="service-form">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Name</label>
                    <Input data-testid="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Email</label>
                      <Input data-testid="service-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Phone</label>
                      <Input data-testid="service-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Message (optional)</label>
                    <Textarea rows={4} data-testid="service-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={`Anything I should know before we start ${service.title}?`} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
                  </div>
                  <Button data-testid="service-submit" disabled={loading} type="submit" className="w-full brand-gradient text-white uppercase tracking-widest text-xs rounded-none py-6 glow-brand">
                    {loading ? 'Sending...' : (<>Book my spot <ArrowRight className="w-4 h-4 ml-2" /></>)}
                  </Button>
                  <p className="text-white/40 text-xs">Zero spam. Details only used for this enquiry.</p>
                </form>
              )}
            </Card>
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="reveal">
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Also on offer</div>
            <h3 className="mt-3 font-display italic text-white text-3xl md:text-4xl uppercase">Explore other services</h3>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {others.map((s) => (
              <Link key={s.slug} to={`/service/${s.slug}`} data-testid={`other-service-${s.slug}`} className="reveal lift group block bg-[#0f0f14] border border-white/5 hover:border-brand transition-colors">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="font-display uppercase text-white text-xl">{s.title}</div>
                  <div className="mt-2 text-white/50 text-xs uppercase tracking-widest">{s.fromPrice}</div>
                  <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-brand">
                    Find out more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
